<template>
  <div>
    <b-overlay
      :show="loadingPage"
      variant="white"
      no-wrap
    />
    <b-button
      id="back-button"
      variant="primary"
      data-test="userProfile-goBackToNext"
      @click="goBackToNext"
    >
      <b-icon-arrow-bar-left/>
      {{ $t('words.goBack') }}
    </b-button>
    <div class="signup-container">
      <div class="app-header">
        <img :alt="$config.client.name" :src="logoPath"/>
      </div>
      <div class="signup-form">
        <h4 class="title">
          {{ $t('profile.title') }}
          <span class="sub-title">{{ $t('profile.subtitle') }}</span>
        </h4>
        <hr class="divider">
        <div class="required-help">* Champs obligatoires</div>
        <ValidationObserver ref="form" v-slot="{ handleSubmit }">
          <b-overlay
            :show="loadingUserInformation"
            rounded="lg"
            :style="'padding: 5px;'"
            variant="white"
          >
            <form>
              <h5>{{ $t('profile.form.personalDetails.title') }}</h5>
              <hr class="divider">
              <div class="row g-2 align-items-center">
                <div class="col-3">
                  <label
                    class="col-form-label left-aligned"
                    for="userProfile-username"
                  >
                    {{ $t('words.username') }}
                  </label>
                </div>
                <div class="col">
                  <div class="input-group flex-nowrap">
                    <input
                      v-model="formUser.username"
                      id="userProfile-username"
                      data-test="userProfile-username"
                      type="text"
                      class="form-control"
                      :placeholder="$t('words.username')"
                      disabled
                    >
                  </div>
                </div>
              </div>
              <div class="row g-2 align-items-center">
                <div class="col-3">
                  <label
                    class="col-form-label left-aligned required"
                    for="userProfile-firstname"
                  >
                    {{ $t('words.firstname') }}
                  </label>
                </div>
                <div class="col">
                  <ValidationProvider ref="first_name" rules="required" v-slot="{ classes, errors }">
                    <div class="control" :class="classes">
                      <input
                        v-model="formUser.first_name"
                        id="userProfile-firstname"
                        data-test="userProfile-firstname"
                        type="text"
                        class="form-control"
                        :placeholder="$t('words.firstname')"
                      >
                      <span class="form-errors">{{ errors[0] }}</span>
                    </div>
                  </ValidationProvider>
                </div>
              </div>
              <div class="row g-2 align-items-center">
                <div class="col-3">
                  <label
                    class="col-form-label left-aligned required"
                    for="userProfile-lastname"
                  >
                    {{ $t('words.lastname') }}
                  </label>
                </div>
                <div class="col">
                  <ValidationProvider ref="last_name" rules="required" v-slot="{ classes, errors }">
                    <div class="control" :class="classes">
                      <input
                        v-model="formUser.last_name"
                        id="userProfile-lastname"
                        data-test="userProfile-lastname"
                        type="text"
                        class="form-control"
                        :placeholder="$t('words.lastname')"
                      >
                      <span class="form-errors">{{ errors[0] }}</span>
                    </div>
                  </ValidationProvider>
                </div>
              </div>
              <div class="row g-2 align-items-center">
                <div class="col-3">
                  <label
                    class="col-form-label left-aligned"
                    for="userProfile-email"
                  >
                    {{ $t('words.email') }}
                  </label>
                </div>
                <div class="col">
                  <ValidationProvider ref="email" rules="required|email" v-slot="{ classes, errors }">
                    <div class="control" :class="classes">
                      <input
                        v-model="formUser.email"
                        id="userProfile-email"
                        data-test="userProfile-email"
                        type="mail"
                        class="form-control"
                        :placeholder="$t('words.email')"
                        disabled
                      >
                      <span class="form-errors">{{ errors[0] }}</span>
                    </div>
                  </ValidationProvider>
                </div>
              </div>
              <div class="row g-2 align-items-center">
                <div class="col-3">
                  <label
                    class="col-form-label left-aligned"
                    for="userProfile-phone"
                  >
                    {{ $t('words.phone') }}
                  </label>
                </div>
                <div class="col">
                  <ValidationProvider ref="phone_number" v-slot="{ classes, errors }">
                    <div class="control" :class="classes">
                      <input
                        v-model="formUser.phone_number"
                        id="userProfile-phone"
                        data-test="userProfile-phone"
                        type="text"
                        class="form-control"
                        placeholder=""
                      >
                      <span class="form-errors">{{ errors[0] }}</span>
                    </div>
                  </ValidationProvider>
                </div>
              </div>
              <div class="row g-2 align-items-center">
                <div class="col-3">
                  <label
                    class="col-form-label left-aligned"
                    for="userProfile-comments"
                  >
                    {{ $t('profile.form.personalDetails.reason') }}
                  </label>
                </div>
                <div class="col">
                  <textarea
                    v-model="formUser.comments"
                    id="userProfile-comments"
                    data-test="userProfile-comments"
                    class="form-control"
                  />
                </div>
              </div>
              <div class="row g-2 align-items-center" style="margin-bottom: 1em;">
                <div class="col-3">
                  <label
                    class="col-form-label left-aligned"
                    style="font-size: 0.87em;"
                    for="userProfile-organisations"
                  >
                    {{ $t('profile.form.personalDetails.organisation.label') }}
                  </label>
                </div>
                <div
                  v-if="userData"
                  class="col"
                  id="userProfile-organisations"
                >
                  <div v-if="userData.usergroup_roles.length === 0">
                    {{ $t('profile.form.personalDetails.organisation.noOrganisation') }}
                  </div>
                  <b-list-group
                    v-else
                  >
                    <b-list-group-item
                      v-for="usergroup of userData.usergroup_roles"
                      :key="usergroup.usergroup.id"
                      :class="'user-role'"
                    >
                      <b>{{ usergroup.usergroup.display_name }}</b>
                      <em> {{ $t('words.as') }} </em>
                      <b>{{ usergroupsRoles.find(el => el.choice === usergroup.role).label }}<span v-if="usergroup.status==0"><br>(en attente de validation)</span></b>
                      
                      <b-button-close
                        class="close"
                        @click="askToRemoveUserFromOrg(usergroup.usergroup)"
                      />
                    </b-list-group-item>
                  </b-list-group>
                </div>
              </div>
              <div class="row g-2 align-items-center">
                <div class="col-3">
                </div>
                <div v-if="userData && $config.client.mail" class="col">
                  <div
                    style="margin: 0 0.5em 0.5em 0.1em; font-size: 0.9em; font-style: italic;"
                  >
                    {{ $t('profile.form.personalDetails.organisation.help') }} 
                    <a :href="`mailto:${$config.client.mail}`">
                      {{ $config.client.mail }}
                    </a>.
                  </div>
                </div>
              </div>
            </form>
            <div class="form-footer">
              <b-button
                :disabled="showCreationForm || (!formUser.first_name || !formUser.last_name || !formUser.email)"
                data-test="userProfile-submitUserInformations"
                @click.prevent="handleSubmit(submitUserInformations)"
                variant="primary"
              >
                {{ $t('words.validate') }}
              </b-button>
            </div>
          </b-overlay>
        </ValidationObserver>
        <b-overlay
          :show="loadingOrganisationCreation"
          rounded="lg"
          :style="'padding: 5px;'"
          variant="white"
        >
          <OrganisationSelector
            v-if="userData"
            ref="organisationSelector"
            :withTitle="false"
            :subtitle="$t('profile.form.personalDetails.organisation.add')"
            :withCreation="true"
            :showCreationForm="showCreationForm"
            @update:showCreationForm="handleShowCreationChange"
            @select="handleOrganisationSelection"
            @create="handleOrganisationCreation"
          />
          <div class="form-footer">
            <b-button
              v-if="showCreationForm"
              data-test="userProfile-cancelCreateOrganisation"
              @click.prevent="showCreationForm = false"
              variant="danger"
            >
              {{ $t('buttons.cancel') }}
            </b-button>
            <b-button
              :disabled="getOrganisationButtonDisabledState()"
              data-test="userProfile-createOrganisation"
              @click.prevent="showCreationForm ?
                createOrganisationAndAttachUser() :
                addUserToOrganisation()
              "
              variant="primary"
            >
              {{ showCreationForm ? $t('buttons.createAttach') : $t('buttons.attach') }}
            </b-button>
          </div>
        </b-overlay>
        <ValidationObserver v-slot="{ handleSubmit }">
          <b-overlay
            :show="loadingUserEmail"
            rounded="lg"
            variant="white"
            :style="'padding: 5px;'"
          >
            <form>
              <h5>{{ $t('profile.form.emailChange.title') }}</h5>
              <hr class="divider">
              <div class="row g-2 align-items-center">
                <div class="col-3" style="padding-right: 0;">
                  <label
                    class="col-form-label left-aligned required"
                    style="font-size: 0.9em;"
                    for="userProfile-newEmail"
                  >
                    {{ $t('profile.form.emailChange.label') }}
                  </label>
                </div>
                <div class="col">
                 <ValidationProvider ref="email" rules="email" v-slot="{ classes, errors }">
                    <div class="control" :class="classes">
                      <input
                        v-model="formEmail.new_email"
                        id="userProfile-newEmail"
                        data-test="userProfile-newEmail"
                        type="mail"
                        class="form-control"
                        :placeholder="$t('words.email')"
                      >
                      <span class="form-errors">{{ errors[0] }}</span>
                    </div>
                  </ValidationProvider>
                </div>
              </div>
              <div class="row g-2 align-items-center">
                <div class="col-3">
                </div>
                <div class="col">
                  <div
                    style="margin: 0 0.5em 0.5em 0.5em; font-size: 0.8em; font-style: italic;"
                  >
                    {{ $t('profile.form.emailChange.help') }}
                  </div>
                </div>
              </div>
            </form>
            <div class="form-footer">
              <b-button
                :disabled="!formEmail.new_email"
                :pressed="btnPressed"
                data-test="userProfile-submitNewEmail"
                @click.prevent="handleSubmit(submitNewEmail)"
                variant="primary"
              >
                {{ $t('words.validate') }}
              </b-button>
            </div>
          </b-overlay>
        </ValidationObserver>
        <ValidationObserver v-slot="{ handleSubmit }">
          <b-overlay
            :show="loadingUserPassword"
            rounded="lg"
            variant="white"
            :style="'padding: 5px;'"
          >
            <form>
              <h5>{{ $t('profile.form.passwordChange.title') }}</h5>
              <hr class="divider">
              <div class="row g-2 align-items-center">
                <div class="col-3" style="padding-right: 0;">
                  <label
                    class="col-form-label left-aligned required"
                    style="font-size: 0.9em;"
                    for="userProfile-password"
                  >
                    {{ $t('profile.form.passwordChange.oldLabel') }}
                  </label>
                </div>
                <div class="col">
                  <ValidationProvider ref="password" rules="required" v-slot="{ classes, errors }">
                    <div class="control" :class="classes">
                      <div class="input-group flex-nowrap">
                        <input
                          v-model="formPassword.password"
                          id="userProfile-password"
                          data-test="userProfile-password"
                          class="form-control"
                          :type="showPassword ? 'text' : 'password'"
                          :placeholder="$t('profile.form.passwordChange.oldLabel')"
                        >
                        <span class="input-group-text">
                          <b-icon
                            :icon="showPassword?'eye-slash-fill':'eye-fill'"
                            data-test="userProfile-toggleShowPassword"
                            :aria-label="$t('ariaLabels.displayPassword')"
                            @click="showPassword = !showPassword"
                          />
                        </span>
                      </div>
                      <span class="form-errors">{{ errors[0] }}</span>
                    </div>
                  </ValidationProvider>
                </div>
              </div>
              <div class="row g-2 align-items-center">
                <div class="col-3" style="padding-right: 0;">
                  <label
                    class="col-form-label left-aligned required"
                    style="font-size: 0.9em;"
                    for="userProfile-newPassword1"
                  >
                      {{ $t('profile.form.passwordChange.newLabel') }}
                    </label>
                </div>
                <div class="col">
                  <ValidationProvider ref="newPassword1" v-slot="{ classes, errors }" vid="confirmation">
                    <div class="control" :class="classes">
                      <div class="input-group flex-nowrap">
                        <input
                          v-model="formPassword.newPassword1"
                          id="userProfile-newPassword1"
                          data-test="userProfile-newPassword1"
                          :type="showNewPassword2 ? 'text' : 'password'"
                          class="form-control"
                          :placeholder="$t('profile.form.passwordChange.newPlaceholder')"
                        >
                        <span class="input-group-text">
                          <b-icon
                            :icon="showNewPassword2?'eye-slash-fill':'eye-fill'"
                            data-test="userProfile-toggleShowNewPassword"
                            :aria-label="$t('ariaLabels.displayPassword')"
                            @click="showNewPassword2 = !showNewPassword2"
                          />
                        </span>
                      </div>
                      <span class="form-errors">{{ errors[0] }}</span>
                    </div>
                  </ValidationProvider>
                </div>
              </div>
              <div class="row g-2 align-items-center">
                <div class="col-3" style="padding-right: 0;">
                  <label
                    class="col-form-label left-aligned"
                    style="font-size: 0.9em;"
                  />
                </div>
                <div class="col">
                  <ValidationProvider ref="newPassword2" rules="confirmed:confirmation" v-slot="{ classes, errors }">
                    <div class="control" :class="classes">
                      <div class="input-group flex-nowrap">
                        <input
                          v-model="formPassword.newPassword2"
                          data-test="userProfile-newPassword2"
                          class="form-control"
                          :type="showNewPassword2 ? 'text' : 'password'"
                          :placeholder="$t('profile.form.passwordChange.confirmPlaceholder')"
                        >
                        <span class="input-group-text">
                          <b-icon
                            data-test="userProfile-toggleShowNewPassword2"
                            :icon="showNewPassword2?'eye-slash-fill':'eye-fill'"
                            alt="Afficher le mot de passe"
                            @click="showNewPassword2 = !showNewPassword2"
                          />
                        </span>
                      </div>
                      <span class="form-errors">{{ errors[0] }}</span>
                    </div>
                  </ValidationProvider>
                </div>
              </div>
              <div class="row g-2 align-items-center">
                <div class="col-3">
                </div>
                <div class="col">
                  <div class="infos">
                    <ul>
                      <li>{{ $t('profile.form.passwordChange.passwordHelp')[0] }}</li>
                      <li>{{ $t('profile.form.passwordChange.passwordHelp')[1] }}</li>
                      <li>{{ $t('profile.form.passwordChange.passwordHelp')[2] }}</li>
                      <li>{{ $t('profile.form.passwordChange.passwordHelp')[3] }}</li>
                    </ul>
                  </div>
                </div>
              </div>
            </form>
            <div class="form-footer">
              <b-button
                :disabled="(!formPassword.password || !formPassword.newPassword1 || !formPassword.newPassword2)"
                :pressed="btnPressed"
                data-test="userProfile-submitNewPassword"
                @click.prevent="handleSubmit(submitNewPassword)"
                variant="primary"
              >
                {{ $t('words.validate') }}
              </b-button>
            </div>
          </b-overlay>
        </ValidationObserver>
        <div v-if="$config.personalDataExport">
          <h5>{{ $t('profile.personalDataExport.title') }}</h5>
          <hr class="divider">
          <PersonalDataExport />
        </div>
        <div>
          <h5>{{ $t('profile.accountDeletion.title') }}</h5>
          <hr class="divider">
          <b-button
            id="delete-account-button"
            variant="danger"
            @click="isAccountDeletionModalOpen = true"
          >
            {{ $t('profile.accountDeletion.button') }}
          </b-button>
        </div>
      </div>
    </div>
    <small class="footer">
      <p>
        {{ $t('footer') }} <a href="https://www.neogeo.fr/" target="_blank" rel="noopener">Neogeo-Technologies</a>
      </p>
    </small>
    <AccountDeletionModal
      :open="isAccountDeletionModalOpen"
      @load="$event => loadingPage = $event"
      @close="isAccountDeletionModalOpen = false"
      @delete="deleteAccount"
    />
  </div>
</template>

<script>
import usergroupsAPI from '@/api/usergroupsAPI';
import i18n from '@/i18n';
import loginAPI from '@/api/loginAPI';


import { notify } from '@/utils.js';
import AccountDeletionModal from '@/components/AccountDeletionModal.vue';
import OrganisationSelector from '@/components/OrganisationSelector.vue';
import PersonalDataExport from '../components/PersonalDataExport.vue';
import { ErrorService } from '@/services/error-service.js';
import modal from '@/utils/modal.js';

import {
  ValidationObserver,
  ValidationProvider,
  extend,
  configure,
} from 'vee-validate';

import { required, email, confirmed } from 'vee-validate/dist/rules';

extend('required', {
  ...required,
  message: () => i18n.t('errors.required')
});

extend('email', {
  ...email,
  message: () => i18n.t('errors.email'),
});

extend('confirmed', {
  ...confirmed,
  message: () => i18n.t('errors.confirmPassword'),
});

configure({
  classes: {
    valid: 'is-valid',
    invalid: 'is-invalid',
  },
});

export default {
  name: 'UserProfile',

  components: {
    AccountDeletionModal,
    OrganisationSelector,
    PersonalDataExport,
    ValidationObserver,
    ValidationProvider,
  },

  data() {
    return {
      organisationsList: [],
      groupsList: [],
      spheresList: [],
      usergroupsRoles: [],
      searchQuery: null,
      next:null,
      userData: null,
      loadingPage: false,
      loadingUserInformation: false,
      loadingUserPassword: false,
      loadingOrganisationCreation: false,
      loadingUserEmail: false,
      formUser: {
        first_name: null,
        last_name: null,
        email: null,
        phone_number: null,
        comments: null,
        username: null
      },
      formEmail: {
        new_email: null
      },
      formPassword: {
        password: null,
        newPassword1: null,
        newPassword2: null
      },
      isOrganisationSelected: false,
      organisation: null,
      btnPressed: false,
      showPassword: false,
      showNewPassword2: false,
      showCreationForm: false,
      organisationSelected: null,
      organisationToCreate: null,
      organisationThumbnail: null,
      organisationSpheres: [],
      isAccountDeletionModalOpen: false
    };
  },

  computed: {
    logoPath() {
      return require(process.env.VUE_APP_LOGO);
    }
  },

  watch: {
    '$i18n.locale': function(newValue, oldValue) {
      if (newValue !== oldValue) {
        this.$refs.form.validate();
      }
    },
  },

  created() {
    this.next=this.$route.query.next || process.env.VUE_APP_NEXT_DEFAULT;
    if(!loginAPI.logged){
      this.$router.push({ name: 'SignIn',query: { next: window.location.pathname}  })
    }
    if (!this.userData) {
      this.loadingUserInformation = true;
      this.getUserDetail();
    }
    if (!this.usergroupsRoles || this.usergroupsRoles.length === 0) {
      this.fetchUsergroupRoles();
    }
  },

  mounted() {
    // Watch modal closing event
    this.$root.$on('bv::modal::hide', () => {
      this.isAccountDeletionModalOpen = false;
    });
  },

  methods: {
    // open confirmation modal via modal utility
    async fetchUsergroupRoles() {
      try {
        const roles = await usergroupsAPI.getUsergroupsRoles();
        this.usergroupsRoles = roles || [];
      } catch (err) {
        console.error(err);
      }
    },

    async createUsergroup(payload) {
      const { form, thumbnail, spheres } = payload;
      const data = {
        organisation: {
          additional_id: form.additional_id,
          display_name: form.display_name,
          codename: form.codename || form.display_name && form.display_name.toLowerCase().replace(/[^a-z0-9]+/g,'-'),
          acronym: form.acronym,
          registration_number: form.registration_number,
          organisation_type: form.organisation_type,
          is_active: true,
          postal_address: form.postal_address,
          description: form.description,
          website_url: form.website_url,
          phone_number: form.phone_number
        },
        display_name: form.display_name,
        codename: form.codename || form.display_name && form.display_name.toLowerCase().replace(/[^a-z0-9]+/g,'-'),
        usergroup_type: 'organisation',
      };
      const createdUsergroup = await usergroupsAPI.createUsergroup(data);
      if (createdUsergroup && createdUsergroup.id) {
        if (thumbnail) {
          await usergroupsAPI.setUsergroupThumbnail(createdUsergroup.id, thumbnail);
        }
        if (spheres && spheres.length > 0) {
          const parentsIds = spheres.map((el) => el.id);
          for (const parentId of parentsIds) {
            await usergroupsAPI.addParentToUsergroup(createdUsergroup.id, parentId);
          }
        }
      }
      return createdUsergroup;
    },

    async getUsergroupMembers(id) {
      try {
        const members = await usergroupsAPI.getUsergroupMembers(id);
        return members && members.results ? members.results : [];
      } catch (err) {
        throw err;
      }
    },

    async addUserToUsergroup({ usergroup, data }) {
      try {
        const response = await usergroupsAPI.addUserToUsergroup(usergroup.id, data);
        if (response) {
          ErrorService.onSuccess(response, `Vous avez bien été ajouté à l\'organisation ${usergroup.display_name}`);
        }
      } catch (err) {
        throw err;
      }
    },

    async removeUserFromUsergroup({ usergroup, userId }) {
      try {
        const response = await usergroupsAPI.removeUserFromUsergroup(usergroup.id, userId);
        if (response) {
          ErrorService.onSuccess(response, `Vous avez bien été retiré de l\'organisation ${usergroup.display_name}`);
        }
      } catch (err) {
        throw err;
      }
    },
    setError(newValue) {
      if (newValue) {
        for (const [key, value] of Object.entries(newValue)) {
          this.$refs[key].applyResult({
            errors: value,
            valid: false,
            failedRules: {}
          });
        }
      }
    },
    getUserDetail() {
      this.loadingUserInformation = true;
      return loginAPI.getUserDetail()
      .then((response) => {
        this.formUser = {
          ...this.formUser,
          ...response
        };
        this.userData = response;
        this.loadingUserInformation = false;
      }).catch((error) => {
            ErrorService.onError(error.response);
              this.loadingUserInformation = false;
            });
    },
    goBackToNext() {
      if (
        this.$config.authorizedRedirections.some(reg => {
          return reg.test(decodeURIComponent(this.next))
        })
      ) {
        this.$router.push(this.$route.path);
        window.location.pathname = this.next;
      } else {
        this.$router.push({ name: 'NotFound' });
      }
    },

    handleShowCreationChange(newValue) {
			this.showCreationForm = newValue;
      this.organisationSelected = null;
			this.organisationToCreate = null;
			if (this.showCreationForm) {
				this.$refs.organisationSelector.clearOrganisation(); // Clear Selector
			}
    },
    handleOrganisationSelection(e) {
      this.organisationSelected = e;
    },
    handleOrganisationCreation(e) {
      this.organisationToCreate = e.organisation;
      this.organisationThumbnail = e.thumbnail;
      this.organisationSpheres = e.spheres;
    },
    getOrganisationButtonDisabledState() {
			if (this.showCreationForm) {
				return !this.organisationToCreate || (this.organisationToCreate && !this.organisationToCreate.isValid);
			} else {
        return this.organisationSelected === null;
      }
    },

    askToRemoveUserFromOrg(e) {
      const removeUserFromOrg = async () => {
        try {
          
          const usergroupMembers = await this.getUsergroupMembers(e.id);
          const userId = usergroupMembers.find(el => el.user.id === this.userData.id).id;
          await this.removeUserFromUsergroup({ usergroup: e, userId: userId });
          await this.getUserDetail();

          this.$refs.organisationSelector.clear();


        } catch (err) {
          console.error(err);
          this.loadingUserInformation = false;
        }
      };

      modal.openConfirmation({
        title: this.$t('modals.removeUserFromOrg.title', { org: e.display_name }),
        content: this.$t('modals.removeUserFromOrg.content'),
        trigger: removeUserFromOrg
      });
    },
    async callAddUserToOrganisation(organisation) {
      try {
          this.loadingOrganisationCreation = true;
          //const usergroup = this.organisationsList.find(el => el.id === organisation.id);
          const data = {
            role: 0,
            status: 0,
            user_id: this.userData.id
          };

          await this.addUserToUsergroup({ usergroup: organisation, data: data });

          await this.getUserDetail();

          this.$refs.organisationSelector.clear();
          

          this.loadingOrganisationCreation = false;
        } catch (err) {
          this.loadingOrganisationCreation = false;
          throw err;
        }
    },
    async addUserToOrganisation() {
      if (this.organisationSelected) { 
        await this.callAddUserToOrganisation(this.organisationSelected);
        this.organisationSelected = null;  
      }
    },

    async createOrganisationAndAttachUser() {
      try {
        this.loadingOrganisationCreation = true;
        const createdOrganisation = await this.createUsergroup({
          form: this.organisationToCreate,
          thumbnail: this.organisationThumbnail,
          spheres: this.organisationSpheres
        });

        this.loadingOrganisationCreation = false;
        this.showCreationForm = false
        await this.callAddUserToOrganisation(createdOrganisation);
      } catch {
        this.searchQuery = null;
        this.loadingOrganisationCreation = false;
      }
    },

    submitUserInformations() {
      this.loadingUserInformation = true;
      loginAPI.updateUserDetail(this.formUser)
        .then(() => {
          this.getUserDetail();
        })
        .catch((error) => {
          ErrorService.onError(error.response);
          this.setError(error.response.data);
          this.loadingUserInformation = false;
        });
    },

    submitNewEmail() {
      const data = {
        new_email: this.formEmail.new_email,
      };
      this.loadingUserEmail = true;
      loginAPI.updateUserDetail(data)
        .then(() => {
          this.loadingUserEmail = false;
          notify(`Un e-mail est envoyé à votre nouvelle adresse. 
              Il contient un lien de validation sur lequel vous devez 
              cliquer pour confirmer le changement. 
            `)
        })
        .catch((error) => {
          ErrorService.onError(error.response);
          this.setError(error.response.data);
          this.loadingUserEmail = false;
        });
    },

    async submitNewPassword() {
      try {
        this.loadingUserPassword = true;
        const data = {
          password: this.formPassword.password,
          new_password: this.formPassword.newPassword2
        };
       let response=await loginAPI.updateUserDetail(data);
        this.loadingUserPassword = false;
        if (response && response.id) {
          notify(`Votre mot de passe a bien été modifié.
              Vous allez être redirigé vers la page de connexion.
            `).then((result) => {
            if (result.isConfirmed) {
              this.$router.push({ name: 'SignIn' });
            }
          });
        }
      } catch (error) {
        console.error(error);
        ErrorService.onError(error.response);
        this.setError(error.response.data);
        this.loadingUserPassword = false;
      }
    },

    async deleteAccount() {
      try {
        this.isAccountDeletionModalOpen = false;
        this.loadingPage = true;
        await loginAPI.deleteUser();
        this.$bvToast.toast(this.$t('profile.accountDeletion.deleteSuccess'), {
          title: this.$t('words.success'),
          variant: 'success',
          autoHideDelay: 5000,
          appendToast: true
        });
        setTimeout(() => {
          this.$router.push({ name: 'SignIn' });
          this.loadingPage = false;
        }, 5000);
      } catch (err) {
        console.error(err);
        this.loadingPage = false;
        this.$bvToast.toast(this.$t('profile.accountDeletion.deleteError'), {
          title: this.$t('words.error'),
          variant: 'danger',
          autoHideDelay: 5000,
          appendToast: true
        });
      }
    }
  }

}
</script>

<style lang="less" scoped>

#back-button {
  font-size: 1.5em;
  position: -webkit-sticky; /* Safari */
  position: sticky;
  top: 5%;
  left: 40px;
  align-self: flex-start;
  border: 2px solid #9BD0FF;
  border-radius: 8px;
  letter-spacing: 1px;
  z-index: 1;
}

.signup-container {
  margin: 1rem auto auto;
  overflow: scroll;
  @media screen and (min-width: 800px) {
    margin: -1rem auto auto;
    width: 800px;
  }
  height: fit-content;
  
  .signup-form {
    margin: 1rem;
    @media screen and (min-width: 800px) {
      margin: 5rem 1rem;
    }

    h4.title {
      color: #373b3d;

      .sub-title {
        font-size: 75%;
        color: #6b7479;
      }
    }

    hr.solid {
      border-top: 2px solid #373b3d;
    }

    #export_data {
      margin-top: 24px;
    }

    h5 {
      margin-bottom: 20px;
      margin-top: 40px;
      color: #373b3d;
    }

    form {
      margin-top: 20px;

      h5 {
        margin-bottom: 20px;
        margin-top: 40px;
        color: #373b3d;
      }

      .row {
        margin-bottom: 1.6rem;
      }

      .input-group {
        span {
          cursor: pointer;
          border-bottom-left-radius: 0;
          border-top-left-radius: 0;
          border-left: none
        }
      }
    }
    .infos {
      font-size: 0.7em;
      font-style: italic;
      margin-right: 1em;
      ul {
        padding-left: 1rem;
      }
    }
    .form-footer {
      display: flex;
      justify-content: flex-end;
      margin-left: 7px;
      margin-top: 30px;
      button {
        margin-left: 2em;
      }
      button.btn-primary {
        border: 2px solid #9BD0FF;
        border-radius: 8px;
      }
      button.btn-outline-secondary {
        background-color: #F7F8FA;
        border: 2px solid #A9B2B9;
        border-radius: 8px;
        color: #2F3234;
      }
      button.btn-outline-secondary:hover {
        color: white;
        background-color: #4b4b4b;
      }
    }
  }
}

#delete-account-button {
  width: 100%;
  font-size: 1rem;
  border: 2px solid #ff9b9b;
  border-radius: 8px;
  font-weight: 600;
}

.user-role {
  display: flex;
  align-items: center;
  justify-content: space-between;
}

.close:hover {
  color: #EB0600;
  transform: scale(1.2);
}

.form-errors {
  color: #EB0600 !important;
}

.form-success {
  color: #30C963 !important;
}

.footer {
  position: relative;
  bottom: 0;
  font-size: small;
  margin-top: 2rem;
}

.footer a {
  text-decoration: none;
}
</style>
